import { Badge } from 'components/Badge/Badge'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import { Button, Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import {
    SHIFT_PROGRAM_STATUS_LABELS,
} from 'types/shiftPrograms.interface'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useState } from 'react'
import { clearShiftPrograms, getShiftPrograms } from 'features/operations/shiftProgramsSlice'
import { IShiftProgramResponse } from 'types/api/operationsShiftProgramsApi.interface'
import { getParams, handlePagination } from 'utils/helpers'
import useQueryPagination from 'hooks/useQueryPagination'
import { NoData } from 'components/NoData'
import { Pagination } from 'components/Pagination/Pagination'
import { OptionsEditIcon } from 'components/Options/Icons/OptionsEditIcon'

const mapFrequencyType = (frequencyType?: string, recurrenceType?: 1 | 2 | 3 | 4) => {
    if (recurrenceType === 2) return 'DAILY'
    if (recurrenceType === 3) return 'WEEKLY'
    if (recurrenceType === 4) return 'CUSTOM'
    if (recurrenceType === 1) return 'EVERY SHIFT'
    if (frequencyType === 'DAILY') return 'DAILY'
    if (frequencyType === 'WEEKLY') return 'WEEKLY'
    if (frequencyType === 'CUSTOM') return 'CUSTOM'
    return 'EVERY SHIFT'
}

const columns: IHonestTableColumn<IShiftProgramResponse>[] = [
    {
        id: 'program',
        header: 'Program',
        fill: true,
        sticky: true,
        minWidth: '16rem',
        cell: (item) => (
            <Cell>
                <BodyText weight='600'>{item.name}</BodyText>
                <OtherText grey>
                    {item.site?.name ?? '-'} · {mapFrequencyType(item.frequencyType, item.recurrenceType)} · {item.shifts?.length ?? 0} shifts
                </OtherText>
            </Cell>
        ),
    },
    {
        id: 'frequency',
        header: 'Frequency',
        minWidth: '8rem',
        cell: (item) => (
            <Badge text={mapFrequencyType(item.frequencyType, item.recurrenceType)} orange />

        ),
    },
    {
        id: 'nextRunAt',
        header: 'Next run',
        align: 'right',
        minWidth: '10rem',
        cell: (item) => (
            <RightCell>
                <OtherText grey>{item.nextRunAt ? new Date(item.nextRunAt).toLocaleString() : '-'}</OtherText>
            </RightCell>
        ),
    },
    {
        id: 'status',
        header: 'Status',
        align: 'right',
        minWidth: '8rem',
        cell: (item) => {
            const status = item.status === 2 ? 'paused' : 'active'
            return (
                <RightCell>
                    <Badge text={SHIFT_PROGRAM_STATUS_LABELS[status]} green={status === 'active'} orange={status === 'paused'} />
                </RightCell>
            )
        },
    },
]

export const ShiftPrograms = () => {
    const navigate = useNavigate()
    const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
    const { data, totalPages } = useSelector((state: RootState) => state.shiftPrograms)
    useQueryPagination({ fetchFunctions: [getShiftPrograms], cleanUpFunctions: [clearShiftPrograms], params })

    return (
        <>
            <NavBar />
            <PageWrapper>
                <Flex between width='100%' mb='16'>
                    <MainHeadline>Shift Programs</MainHeadline>
                    <Button orange onClick={() => navigate('add')}>Add Program</Button>
                </Flex>
                {data?.length ? (
                    <HonestTable
                        columns={columns}
                        data={data}
                        keyField='id'
                        onClick={(item) => navigate(`${item.id}`)}
                        menuOptions={(): IHonestTableMenuOption<IShiftProgramResponse>[] => [
                            { label: 'Edit', icon: <OptionsEditIcon />, onClick: (row) => navigate(`${row.id}/edit`) },
                        ]}
                    />
                ) : (
                    <NoData text='No shift programs to display' />
                )}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
